import { io } from 'socket.io-client';
import Axios from '../../networking/intraceptor';

let socket = null;
let refreshTimer = null;

const KNOWN_EVENTS = [
	'ready',
	'notification:new',
	'order:updated',
	'prescription:new',
	'incoming-order:new',
	'mobile-refill-request-created',
	'mobile-prescription-order-created',
	'pharmacy-patient-chat-message',
	'pharmacy-patient-chat-read',
	'patient-profile-updated',
	'pharmacist-signature:completed',
	'safe-supply-missing-iv-code-review',
	'rx-quantity-change-reasons-saved',
	'delivered-rx-reason-change-review',
	'reprocessed-delivery-review',
	'support-chat-updated',
	'pharmacy-user-presence-updated',
];

const DEFAULT_TOKEN_TTL_SECONDS = 3600;
const MIN_REFRESH_DELAY_SECONDS = 10;
const REFRESH_RETRY_SECONDS = 30;

export function subscribeRealtimeEvent(eventName, callback) {
	const handler = (event) => {
		const detail = event?.detail || {};
		if (detail.event !== eventName) {
			return;
		}

		callback?.({
			name: detail.event,
			data: detail.payload,
			rooms: detail.rooms || [],
		});
	};

	window.addEventListener('oatrx:realtime:event', handler);

	return {
		close: () => window.removeEventListener('oatrx:realtime:event', handler),
	};
}

export function getRealtimeSocket() {
	return socket;
}

export function emitRealtimeSocketEvent(eventName, payload = {}) {
	if (!socket || !socket.connected) {
		return false;
	}

	socket.emit(eventName, payload);
	return true;
}

export function subscribeRealtimeStatus(callback) {
	const handler = (event) => callback?.(event?.detail || {});
	window.addEventListener('oatrx:realtime:status', handler);

	return {
		close: () => window.removeEventListener('oatrx:realtime:status', handler),
	};
}

function emitRealtimeStatus(detail) {
	window.dispatchEvent(new CustomEvent('oatrx:realtime:status', { detail }));
}

function clearRefreshTimer() {
	if (refreshTimer) {
		window.clearTimeout(refreshTimer);
		refreshTimer = null;
	}
}

function getRefreshDelaySeconds(ttlSeconds) {
	const ttl = Number(ttlSeconds) || DEFAULT_TOKEN_TTL_SECONDS;
	const buffer = ttl > 600 ? 300 : Math.max(30, Math.floor(ttl * 0.2));

	return Math.max(MIN_REFRESH_DELAY_SECONDS, ttl - buffer);
}

async function getRealtimeToken() {
	const response = await Axios.post('realtime/token');
	const { token, rooms = [], realtimeUrl, ttlSeconds, environment, roomPrefix } = response?.data || {};

	if (!token) {
		throw new Error('Realtime token endpoint did not return a token.');
	}

	return {
		token,
		rooms,
		realtimeUrl: realtimeUrl || '',
		ttlSeconds: ttlSeconds || DEFAULT_TOKEN_TTL_SECONDS,
		environment: environment || 'staging',
		roomPrefix: roomPrefix || `${environment || 'staging'}:`,
	};
}

export async function connectRealtimeDemo({ onStatus, onEvent } = {}) {
	let closed = false;

	const emitStatus = (detail) => {
		onStatus?.(detail);
	};

	const scheduleRefreshRetry = (statusBase) => {
		clearRefreshTimer();
		refreshTimer = window.setTimeout(() => {
			if (closed) {
				return;
			}

			createConnection({ refresh: true }).catch((retryError) => {
				emitStatus({
					status: 'refresh_error',
					message: retryError?.response?.data?.message || retryError?.message || 'Realtime token refresh failed',
					...statusBase,
				});
				scheduleRefreshRetry(statusBase);
			});
		}, REFRESH_RETRY_SECONDS * 1000);
	};

	const createConnection = async ({ refresh = false } = {}) => {
		const tokenDetail = await getRealtimeToken();
		const { token, rooms, realtimeUrl, ttlSeconds, environment, roomPrefix } = tokenDetail;

		if (closed) {
			return;
		}

		const previousSocket = socket;
		const nextSocket = io(realtimeUrl, {
			auth: { token },
			transports: ['websocket'],
		});

		const statusBase = {
			rooms,
			ttlSeconds,
			environment,
			roomPrefix,
			refresh,
			nextRefreshSeconds: getRefreshDelaySeconds(ttlSeconds),
		};

		const emitEvent = (event, payload) => {
			const detail = { event, payload, rooms, environment, roomPrefix };
			onEvent?.(detail);
			window.dispatchEvent(new CustomEvent('oatrx:realtime:event', { detail }));
		};

		nextSocket.on('connect', () => {
			socket = nextSocket;

			if (previousSocket && previousSocket !== nextSocket) {
				previousSocket.removeAllListeners();
				previousSocket.close();
			}

			emitStatus({
				status: 'connected',
				socketId: nextSocket.id,
				...statusBase,
			});
			emitRealtimeStatus({
				status: 'connected',
				socketId: nextSocket.id,
				rooms,
				environment,
				roomPrefix,
			});
		});

		nextSocket.on('disconnect', (reason) => {
			if (socket === nextSocket) {
				emitStatus({
					status: 'disconnected',
					reason,
					...statusBase,
				});
				emitRealtimeStatus({
					status: 'disconnected',
					socketId: nextSocket.id,
					reason,
					rooms,
					environment,
					roomPrefix,
				});
			}
		});

		nextSocket.on('connect_error', (error) => {
			emitStatus({
				status: refresh ? 'refresh_error' : 'connect_error',
				message: error?.message,
				...statusBase,
			});

			if (refresh) {
				nextSocket.removeAllListeners();
				nextSocket.close();
				scheduleRefreshRetry(statusBase);
			}
		});

		KNOWN_EVENTS.forEach((event) => {
			nextSocket.on(event, (payload) => emitEvent(event, payload));
		});

		nextSocket.onAny((event, ...args) => {
			if (!KNOWN_EVENTS.includes(event)) {
				emitEvent(event, args.length > 1 ? args : args[0]);
			}
		});

		if (!refresh) {
			socket = nextSocket;
		}

		clearRefreshTimer();
		refreshTimer = window.setTimeout(() => {
			if (closed) {
				return;
			}

			emitStatus({
				status: 'refreshing',
				...statusBase,
			});

			createConnection({ refresh: true }).catch((error) => {
				emitStatus({
					status: 'refresh_error',
					message: error?.response?.data?.message || error?.message || 'Realtime token refresh failed',
					...statusBase,
				});
				scheduleRefreshRetry(statusBase);
			});
		}, getRefreshDelaySeconds(ttlSeconds) * 1000);
	};

	await createConnection();

	return () => {
		closed = true;
		clearRefreshTimer();

		if (socket) {
			socket.removeAllListeners();
			socket.close();
			socket = null;
		}
	};
}
